/**
 * Role-based route access control configuration.
 * Defines which routes each user role can access.
 *
 * @module router/roleAccess
 */
/**
 * Session-scoped cache for route access checks.
 * Key format: `${routeName}:${userRole}`
 * Cleared on logout or role change.
 */
const routeAccessCache = new Map();
/**
 * Checks if a route is allowed for a given user role.
 * Results are cached per session for performance.
 *
 * All non-public routes MUST define allowedRoles in their meta.
 * If allowedRoles is missing, access is denied by default.
 *
 * @param routeName - The name of the route to check
 * @param userRole - The user's role (planner, manager, admin, etc.)
 * @param allowedRoles - Array of allowed roles from route meta (required)
 * @returns True if the route is allowed for the role, false otherwise
 *
 * @example
 * isRouteAllowedForRole('planning', 'planner', ['planner', 'manager', 'admin']) // true
 * isRouteAllowedForRole('asset_dashboard', 'planner', ['manager', 'admin']) // false
 * isRouteAllowedForRole('some_route', 'admin', null) // false (missing allowedRoles)
 */
export function isRouteAllowedForRole(routeName, userRole, allowedRoles) {
    // If no route name or role provided, deny access by default
    if (!routeName || !userRole) {
        return false;
    }
    // If no allowedRoles defined, deny access (all routes must have explicit permissions)
    if (!allowedRoles || !Array.isArray(allowedRoles)) {
        console.warn(`Route "${routeName}" is missing allowedRoles meta. Access denied.`);
        return false;
    }
    // Generate cache key
    const cacheKey = `${routeName}:${userRole}`;
    // Check cache first
    const cached = routeAccessCache.get(cacheKey);
    if (cached !== undefined) {
        return cached;
    }
    // Check if user's role is in the allowed roles
    const isAllowed = allowedRoles.includes(userRole);
    // Cache the result
    routeAccessCache.set(cacheKey, isAllowed);
    return isAllowed;
}
/**
 * Resolves the effective role based on module flags.
 * When only Planning module is enabled (Live OEE disabled),
 * all roles inherit planner permissions.
 *
 * @param userRole - The user's actual role
 * @param isLiveOEEEnabled - Whether Live OEE module is enabled
 * @param isPlanningEnabled - Whether Planning module is enabled
 * @returns The effective role to use for permission checks
 *
 * @example
 * resolveEffectiveRole('viewer', false, true) // 'planner' (Planning-only mode)
 * resolveEffectiveRole('viewer', true, true) // 'viewer' (both modules enabled)
 * resolveEffectiveRole('admin', false, true) // 'planner' (Planning-only mode)
 */
export function resolveEffectiveRole(userRole, isLiveOEEEnabled, isPlanningEnabled) {
    // Return original role if invalid/empty
    if (!userRole) {
        return userRole;
    }
    // If Live OEE is disabled but Planning is enabled,
    // treat all roles as having planner permissions
    if (!isLiveOEEEnabled && isPlanningEnabled) {
        return 'planner';
    }
    // Otherwise, use the actual user role
    return userRole;
}
/**
 * Enhanced version of isRouteAllowedForRole that considers module flags.
 * Resolves the effective role based on enabled modules before checking permissions.
 *
 * @param routeName - The name of the route to check
 * @param userRole - The user's role (planner, manager, admin, etc.)
 * @param allowedRoles - Array of allowed roles from route meta (required)
 * @param isLiveOEEEnabled - Whether Live OEE module is enabled
 * @param isPlanningEnabled - Whether Planning module is enabled
 * @returns True if the route is allowed for the effective role, false otherwise
 *
 * @example
 * // In Planning-only mode, viewer gets planner permissions
 * isRouteAllowedForRoleWithModules('planning', 'viewer', ['planner'], false, true) // true
 * // With both modules enabled, viewer doesn't have planner permissions
 * isRouteAllowedForRoleWithModules('planning', 'viewer', ['planner'], true, true) // false
 */
export function isRouteAllowedForRoleWithModules(routeName, userRole, allowedRoles, isLiveOEEEnabled, isPlanningEnabled) {
    const effectiveRole = resolveEffectiveRole(userRole, isLiveOEEEnabled, isPlanningEnabled);
    return isRouteAllowedForRole(routeName, effectiveRole, allowedRoles);
}
/**
 * Clears the route access cache.
 * Should be called on logout or when user role changes to ensure
 * cache doesn't return stale results for a different user session.
 *
 * @example
 * // Call in auth store logoutAction
 * clearRouteAccessCache();
 */
export function clearRouteAccessCache() {
    routeAccessCache.clear();
}
/**
 * Gets the size of the current route access cache.
 * Useful for debugging and testing.
 *
 * @returns The number of cached entries
 */
export function getRouteAccessCacheSize() {
    return routeAccessCache.size;
}
